import React, { useEffect, useMemo, useState } from 'react';
import { TournamentLink } from 'views/Tournament/TournamentLink';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TableSortLabel, Tooltip, Typography, Button } from '@material-ui/core';
import moment from 'moment';
import clsx from 'clsx';
import { IPve, IPveParticipant } from '@pvplevelup/core/dist/interfaces/Pve/IPve';
import { IPvePull, IPvePullBreakdown } from '@pvplevelup/core/dist/interfaces/Pve/IPvePull';
import { getPveInstance, pveInstanceHasHardMode, describePveHardModeRule } from '@pvplevelup/core/dist/interfaces/Pve/IPveInstance';
import { getPveRunVerification } from '@pvplevelup/core/dist/pve/PveRunVerification';
import { CloudUpload, Whatshot } from '@material-ui/icons';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { GameStatus } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { classImages } from 'utilities/ClassImages';
import { PveIcons } from 'utilities/pve_icons';
import {
  formatPveDuration, formatPveNumber, getPullColor, getPveDamageColor, getPullLabel, getPveInstanceName, getPveRunState, PULL_COLORS,
} from './pveFormat';
import { PveBreakdown } from './PveBreakdown';
import { RoleSpecTags } from '../RoleSpecTags';
import { VerifiedBadge } from './VerifiedBadge';

const MUTED = 'rgba(255, 255, 255, 0.6)';
const DIVIDER = '1px solid rgba(255, 255, 255, 0.07)';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  panel: {
    position: 'relative',
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(26, 27, 32, 0.95), rgba(18, 18, 22, 0.95))',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.28)',
  },
  panelHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.5, 2.25),
    borderBottom: DIVIDER,
    background: 'rgba(255, 255, 255, 0.02)',
  },
  panelTitle: {
    fontSize: '0.75rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  panelTitleStrong: {
    fontSize: '1rem',
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: 'none',
    color: '#fff',
  },
  panelHint: {
    marginLeft: 8,
    fontSize: '0.8rem',
    fontWeight: 400,
    letterSpacing: 0,
    textTransform: 'none',
    color: MUTED,
  },
  panelBody: {
    padding: theme.spacing(1.75, 2.25),
  },
  banner: {
    position: 'relative',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.35)',
  },
  bannerShade: {
    background: 'linear-gradient(90deg, rgba(12, 12, 16, 0.95) 0%, rgba(12, 12, 16, 0.75) 55%, rgba(12, 12, 16, 0.35) 100%)',
    padding: theme.spacing(2.75, 3),
    minHeight: 150,
    display: 'grid',
    alignContent: 'center',
  },
  kicker: {
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#90caf9',
  },
  title: {
    marginTop: 2,
    fontSize: '1.9rem',
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    marginTop: 4,
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.75)',
  },
  chips: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    justifyContent: 'start',
    alignItems: 'center',
    marginTop: theme.spacing(1.5),
  },
  tag: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 10,
    fontSize: '0.75rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.9)',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    backdropFilter: 'blur(4px)',
  },
  stats: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 10,
    justifyContent: 'start',
    marginTop: theme.spacing(2),
  },
  stat: {
    minWidth: 92,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.35)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  statValue: {
    fontSize: '1.15rem',
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
  },
  statLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  encounters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
    gridGap: theme.spacing(1),
  },
  encounter: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 10,
    alignItems: 'center',
    padding: theme.spacing(1, 1.25),
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.07)',
    transition: 'background 0.15s, border-color 0.15s',
  },
  encounterActive: {
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.07)',
      borderColor: 'rgba(66, 165, 245, 0.4)',
    },
  },
  encounterDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
  },
  encounterName: {
    fontWeight: 700,
    fontSize: '0.92rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  encounterMeta: {
    marginTop: 1,
    fontSize: '0.78rem',
    color: MUTED,
  },
  encounterTag: {
    marginLeft: 6,
    padding: '0 6px',
    borderRadius: 6,
    fontSize: '0.65rem',
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#ffcc80',
    background: 'rgba(255, 167, 38, 0.14)',
  },
  timeline: {
    display: 'flex',
    height: 30,
    borderRadius: 8,
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    marginBottom: theme.spacing(1.5),
  },
  segment: {
    height: '100%',
    cursor: 'pointer',
    borderRight: '1px solid rgba(0, 0, 0, 0.6)',
    minWidth: 3,
    transition: 'filter 0.1s, opacity 0.15s',
    '&:hover': {
      filter: 'brightness(1.3)',
    },
  },
  // each chip as wide as its label, wrapping onto more rows (no flexbox gap in Electron 8: margins instead)
  pullChips: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: -6,
    '& > *': {
      marginRight: 6,
      marginBottom: 6,
    },
  },
  pullChip: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 6,
    alignItems: 'center',
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: '3px 10px 3px 8px',
    borderRadius: 10,
    fontSize: '0.78rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid',
    transition: 'background 0.12s, opacity 0.12s',
  },
  // a name too long for the whole row ends in an ellipsis (hover for the full name)
  pullLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pullDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  toolButton: {
    height: 30,
    padding: '0 12px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'rgba(255, 255, 255, 0.06)',
    '&:hover': {
      color: '#fff',
      background: 'rgba(255, 255, 255, 0.12)',
    },
  },
  toolButtonActive: {
    color: '#fff',
    background: 'rgba(66, 165, 245, 0.2)',
    boxShadow: `inset 0 0 0 1px rgba(66, 165, 245, 0.6)`,
  },
  tools: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 8,
  },
  bossLine: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 14,
    justifyContent: 'start',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 8,
    fontSize: '0.82rem',
    background: 'rgba(255, 255, 255, 0.03)',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  meter: {
    // sortable headers
    '& .MuiTableSortLabel-root': {
      color: 'inherit',
      '&:hover': {
        color: '#fff',
      },
    },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
      color: '#fff',
    },
    '& .MuiTableSortLabel-icon': {
      color: `#42a5f5 !important`,
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
    '& .MuiTableCell-head': {
      fontSize: '0.7rem',
      fontWeight: 800,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: MUTED,
      whiteSpace: 'nowrap',
    },
    '& .MuiTableRow-root:hover .MuiTableCell-body': {
      background: 'rgba(66, 165, 245, 0.04)',
    },
  },
  number: {
    fontVariantNumeric: 'tabular-nums',
  },
  player: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    alignItems: 'center',
    whiteSpace: 'nowrap',
    fontWeight: 600,
  },
  classIcon: {
    width: 22,
    height: 22,
    display: 'block',
    borderRadius: 5,
  },
  bar: {
    position: 'relative',
    minWidth: 120,
    padding: '2px 0',
  },
  barFill: {
    position: 'absolute',
    left: 0,
    top: 1,
    bottom: 1,
    borderRadius: 5,
    opacity: 0.3,
  },
  barText: {
    position: 'relative',
    paddingLeft: 8,
    fontVariantNumeric: 'tabular-nums',
  },
  '@keyframes waitPulse': {
    '0%': {boxShadow: '0 0 0 0 rgba(66, 165, 245, 0.6)'},
    '70%': {boxShadow: '0 0 0 10px rgba(66, 165, 245, 0)'},
    '100%': {boxShadow: '0 0 0 0 rgba(66, 165, 245, 0)'},
  },
  waitCard: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 16,
    alignItems: 'start',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderRadius: 14,
    background: 'linear-gradient(135deg, rgba(66, 165, 245, 0.14), rgba(66, 165, 245, 0.03))',
    border: '1px solid rgba(66, 165, 245, 0.4)',
  },
  waitPulse: {
    width: 14,
    height: 14,
    marginTop: 4,
    borderRadius: '50%',
    background: '#42a5f5',
    animation: '$waitPulse 1.8s infinite',
  },
  waitTitle: {
    fontSize: '0.98rem',
    fontWeight: 800,
  },
  waitText: {
    marginTop: 3,
    fontSize: '0.84rem',
    color: 'rgba(255, 255, 255, 0.75)',
  },
  waitPlayers: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, max-content))',
    gridGap: 6,
    marginTop: theme.spacing(1.25),
  },
  waitPlayer: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 6,
    alignItems: 'center',
    justifyContent: 'start',
    padding: '3px 10px 3px 6px',
    borderRadius: 10,
    fontSize: '0.8rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    background: 'rgba(255, 255, 255, 0.04)',
  },
  hardInfo: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 14,
    alignItems: 'start',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    borderRadius: 14,
    background: 'linear-gradient(135deg, rgba(255, 112, 67, 0.12), rgba(255, 112, 67, 0.03))',
    border: '1px solid rgba(255, 112, 67, 0.35)',
  },
  hardInfoInvalid: {
    background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.14), rgba(239, 83, 80, 0.04))',
    borderColor: 'rgba(239, 83, 80, 0.5)',
  },
  hardIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    color: '#ff8a65',
    background: 'rgba(255, 112, 67, 0.16)',
    border: '1px solid rgba(255, 112, 67, 0.35)',
  },
  hardTitle: {
    fontSize: '0.95rem',
    fontWeight: 800,
  },
  hardText: {
    marginTop: 3,
    fontSize: '0.86rem',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  hardMeta: {
    marginTop: 6,
    fontSize: '0.8rem',
    color: MUTED,
  },
  empty: {
    padding: theme.spacing(2, 0),
    fontSize: '0.9rem',
    color: MUTED,
  },
}));

const ROLE_COLORS: Record<string, string> = {Tank: '#42a5f5', Healer: '#66bb6a', DPS: '#ef5350'};

export const RoleChip: React.FC<{role?: string | null}> = ({role}) => {
  if(!role)
    return null;
  const color = ROLE_COLORS[role] || '#9e9e9e';
  return (
    <span style={{display: 'inline-block', padding: '0 7px', borderRadius: 7, fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.04em',
      lineHeight: '17px', color, background: `${color}24`, border: `1px solid ${color}66`}}>{role}</span>
  );
};

interface MeterRow {
  name: string;
  damage: number;
  bossDamage?: number;
  heal: number;
  dTaken: number;
  deaths: number;
  dps: number;
  hps: number;
}

/** marks a member whose app sends the combat log for the run (see IPve.senders) */
export const SenderMark: React.FC<{live?: boolean}> = ({live}) => (
  <Tooltip title={live ? 'Sending the combat log for this run' : 'Sent the combat log for this run'}>
    <CloudUpload style={{fontSize: 15, color: '#4fc3f7', verticalAlign: 'middle'}} />
  </Tooltip>
);

const PlayerCell: React.FC<{name: string, player?: IPveParticipant, sending?: boolean, live?: boolean}> = ({name, player, sending, live}) => {
  const classes = useStyles();
  return (
    <span className={classes.player}>
      {player?.class && <img className={classes.classIcon} src={classImages.getColorIcon(player.class as AocClasses)} title={player.class} />}
      <span>{name}</span>
      <RoleChip role={player?.role} />
      <RoleSpecTags role={player?.role} spec={player?.roleSpec} />
      {sending && <SenderMark live={live} />}
      {player?.adHoc && <Tooltip title="Doesn't use the app; tracked from the group's logs"><span style={{color: MUTED, fontSize: '0.75em', fontWeight: 400}}>ad-hoc</span></Tooltip>}
    </span>
  );
}

const BarCell: React.FC<{value: number, max: number, color: string, text: string}> = ({value, max, color, text}) => {
  const classes = useStyles();
  return (
    <div className={classes.bar}>
      <div className={classes.barFill} style={{width: `${max > 0 ? Math.round((value / max) * 100) : 0}%`, background: color}} />
      <span className={classes.barText}>{text}</span>
    </div>
  );
}

/** Damage / healing / taken per player for the whole run or one pull. */
type MeterSortKey = 'name' | 'damage' | 'dps' | 'bossDamage' | 'heal' | 'hps' | 'dTaken' | 'deaths';

export const PveMeter: React.FC<{rows: MeterRow[], players: IPveParticipant[], showBossDamage?: boolean, senders?: string[], live?: boolean}> = ({rows: unsorted, players, showBossDamage, senders, live}) => {
  const classes = useStyles();
  // click a header to sort by it, again to flip; numbers start highest first, names A-Z
  const [sort, setSort] = useState<{key: MeterSortKey, desc: boolean}>({key: 'damage', desc: true});
  const rows = [...unsorted].sort((a, b) => {
    const diff = sort.key == 'name' ? a.name.localeCompare(b.name) : ((a as any)[sort.key] || 0) - ((b as any)[sort.key] || 0);
    return sort.desc ? -diff : diff;
  });
  const sortCell = (key: MeterSortKey, label: string, align?: 'right') => (
    <TableCell align={align} sortDirection={sort.key == key ? (sort.desc ? 'desc' : 'asc') : false}>
      <TableSortLabel active={sort.key == key} direction={sort.key == key && !sort.desc ? 'asc' : 'desc'}
        onClick={() => setSort((current) => current.key == key ? {key, desc: !current.desc} : {key, desc: key != 'name'})}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
  const maxDamage = Math.max(0, ...rows.map((r) => r.damage));
  const maxHeal = Math.max(0, ...rows.map((r) => r.heal));
  const maxTaken = Math.max(0, ...rows.map((r) => r.dTaken));
  return (
    <TableContainer>
      <Table size="small" className={classes.meter}>
        <TableHead>
          <TableRow>
            {sortCell('name', 'Player')}
            {sortCell('damage', 'Damage')}
            {sortCell('dps', 'DPS', 'right')}
            {showBossDamage && sortCell('bossDamage', 'Boss Damage', 'right')}
            {sortCell('heal', 'Healing')}
            {sortCell('hps', 'HPS', 'right')}
            {sortCell('dTaken', 'Damage Taken')}
            {sortCell('deaths', 'Deaths', 'right')}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const player = players.find((p) => p.name == row.name);
            return (
              <TableRow key={row.name}>
                <TableCell><PlayerCell name={row.name} player={player} sending={(senders || []).indexOf(row.name) != -1} live={live} /></TableCell>
                <TableCell><BarCell value={row.damage} max={maxDamage} color={getPveDamageColor(player?.class)} text={formatPveNumber(row.damage)} /></TableCell>
                <TableCell align="right" className={classes.number} style={{fontWeight: 700}}>{formatPveNumber(row.dps)}</TableCell>
                {showBossDamage && <TableCell align="right" className={classes.number}>{formatPveNumber(row.bossDamage)}</TableCell>}
                <TableCell><BarCell value={row.heal} max={maxHeal} color="#43a047" text={formatPveNumber(row.heal)} /></TableCell>
                <TableCell align="right" className={classes.number} style={{fontWeight: 700}}>{formatPveNumber(row.hps)}</TableCell>
                <TableCell><BarCell value={row.dTaken} max={maxTaken} color="#1e88e5" text={formatPveNumber(row.dTaken)} /></TableCell>
                <TableCell align="right" className={classes.number} style={{color: row.deaths > 0 ? '#ef9a9a' : MUTED, fontWeight: row.deaths > 0 ? 700 : 400}}>{row.deaths}</TableCell>
              </TableRow>
            );
          })}
          {rows.length == 0 &&
            <TableRow><TableCell colSpan={showBossDamage ? 8 : 7} style={{color: MUTED}}>Nothing recorded yet.</TableCell></TableRow>
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** The selected pulls added together; per-second numbers are over their combined length. */
const combinedRows = (pulls: IPvePull[]): MeterRow[] => {
  const duration = pulls.reduce((sum, pull) => sum + pull.duration, 0);
  const rows: Record<string, MeterRow> = {};
  pulls.forEach((pull) => pull.players.forEach((p) => {
    const row = rows[p.name] || (rows[p.name] = {name: p.name, damage: 0, bossDamage: 0, heal: 0, dTaken: 0, deaths: 0, dps: 0, hps: 0});
    row.damage += p.damage;
    row.bossDamage = (row.bossDamage || 0) + p.bossDamage;
    row.heal += p.heal;
    row.dTaken += p.dTaken;
    row.deaths += p.deaths;
  }));
  return Object.keys(rows).map((name) => {
    const row = rows[name];
    row.dps = duration > 0 ? row.damage / duration : 0;
    row.hps = duration > 0 ? row.heal / duration : 0;
    return row;
  }).sort((a, b) => b.damage - a.damage);
};

/** Re-renders every second while the run is live, for the elapsed clock. */
function useNow(live: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if(!live)
      return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [live]);
  return now;
}

interface Props {
  pve: IPve;
  /** already in memory (Boss Explorer preview); otherwise they're loaded from the server */
  breakdowns?: IPvePullBreakdown[];
}

export const PveRunView: React.FC<Props> = ({pve, breakdowns}) => {
  const classes = useStyles();
  const live = pve.status == GameStatus.Active;
  const now = useNow(live);
  const pulls = pve.pulls || [];
  const players = pve.players || [];
  // null: every pull, including ones still to come in a live run
  const [selection, setSelection] = useState<number[] | null>(null);
  const allIndexes = pulls.map((p) => p.index);
  // a pull index can disappear (a boss pull folding back into trash), so only keep the ones that exist
  const selected = selection == null ? allIndexes : selection.filter((index) => allIndexes.indexOf(index) != -1);
  const selectedPulls = pulls.filter((p) => selected.indexOf(p.index) != -1);
  const isSelected = (index: number) => selected.indexOf(index) != -1;
  const toggle = (index: number) => setSelection(isSelected(index) ? selected.filter((i) => i != index) : selected.concat([index]).sort((a, b) => a - b));
  const selectedDuration = selectedPulls.reduce((sum, pull) => sum + pull.duration, 0);
  const instance = getPveInstance(pve.instance || 0);
  const state = getPveRunState(pve);

  // a run counts from the first damage inside the instance (tracking_start), not from when it was started
  const waiting = live && !pve.tracking_start;
  const elapsed = live ? (pve.tracking_start ? (now - moment(pve.tracking_start).valueOf()) / 1000 : 0) : pve.timeElapsed;
  const appUsers = players.filter((p) => !p.adHoc && !p.left && !p.kicked);
  // before the start: the app users sending from inside; after it: the owner and up to 6 others picked by the server
  const senders = pve.senders || [];
  const runStart = pulls.length > 0 ? pulls[0].start_time : 0;
  const runEnd = pulls.length > 0 ? Math.max(...pulls.map((p) => p.end_time)) : 0;
  const span = Math.max(1, runEnd - runStart);

  const encounterStats = useMemo(() => (instance?.encounters || []).map((encounter) => {
    const attempts = pulls.filter((p) => p.type == 'boss' && (p.encounters || []).indexOf(encounter.id) != -1);
    const kill = attempts.find((p) => p.result == 'kill');
    return {encounter, attempts: attempts.length, kill};
  }), [instance, pulls]);


  const hasHardMode = pveInstanceHasHardMode(instance);
  const verification = getPveRunVerification(pve);
  const invalid = verification?.invalid == true;
  const hardModeMissed = invalid && hasHardMode && !pve.hardMode;
  const finalBoss = instance?.encounters.find((e) => e.final)?.name;
  const detectedText = pve.hardModeEvidence ? `${pve.hardModeEvidence.detail}${pve.hardModeEvidence.time ? ` at ${moment(pve.hardModeEvidence.time).format('HH:mm:ss')}` : ''}` : '';

  return (
    <Box>
      <div className={classes.banner} style={{backgroundImage: PveIcons[pve.instance || 0] ? `url(${PveIcons[pve.instance || 0]})` : undefined}}>
        <div className={classes.bannerShade}>
          <div className={classes.kicker}>{instance ? (instance.solo ? 'Solo instance' : `Up to ${instance.maxPlayers} players`) : 'Dungeon run'}</div>
          <div className={classes.title}>{getPveInstanceName(pve)}</div>
          <div className={classes.subtitle}>
            {pve.name}{pve.server ? ` · ${pve.server}` : ''}{pve.start_time ? ` · ${moment(pve.start_time).format('MMM Do YYYY, h:mm a')}` : ''}
          </div>
          <div className={classes.chips}>
            <span className={classes.tag} style={{color: '#fff', background: state.color, borderColor: state.color}}>{state.label}</span>
            <VerifiedBadge pve={pve} />
            <TournamentLink tournament={(pve as any).tournament} style={{marginLeft: 6}} />
            {pve.hasAdHoc && <span className={classes.tag}>Includes players without the app</span>}
          </div>
          <div className={classes.stats}>
            <div className={classes.stat}>
              <div className={classes.statValue} style={pve.completed ? {color: '#81c784'} : undefined}>{formatPveDuration(pve.completed ? pve.clearTime : elapsed)}</div>
              <div className={classes.statLabel}>{pve.completed ? 'Clear time' : live ? 'Elapsed' : 'Run time'}</div>
            </div>
            {encounterStats.length > 0 &&
              <div className={classes.stat}>
                <div className={classes.statValue}>{encounterStats.filter((e) => e.kill).length}<span style={{color: MUTED, fontSize: '0.8em'}}>/{encounterStats.filter((e) => !e.encounter.optional).length}</span></div>
                <div className={classes.statLabel}>Bosses</div>
              </div>
            }
            <div className={classes.stat}>
              <div className={classes.statValue}>{pulls.length}</div>
              <div className={classes.statLabel}>Pulls</div>
            </div>
            <div className={classes.stat}>
              <div className={classes.statValue}>{pulls.filter((p) => p.type == 'boss' && p.result == 'wipe').length}</div>
              <div className={classes.statLabel}>Wipes</div>
            </div>
            <div className={classes.stat}>
              <div className={classes.statValue}>{players.length}</div>
              <div className={classes.statLabel}>Players</div>
            </div>
          </div>
        </div>
      </div>

      {waiting &&
        <div className={classes.waitCard}>
          <div className={classes.waitPulse} />
          <div style={{minWidth: 0}}>
            <div className={classes.waitTitle}>Waiting for the first fight in {getPveInstanceName(pve)}</div>
            <div className={classes.waitText}>
              The run starts at the first damage dealt or taken inside the instance, even if not everyone is in yet.
              Nothing before that is recorded, and if nothing is fought the run is discarded.
            </div>
            <div className={classes.waitPlayers}>
              {appUsers.map((p) => {
                const ok = senders.indexOf(p.name || '') != -1;
                return (
                  <span key={p.name} className={classes.waitPlayer} style={ok ? {color: '#a5d6a7', borderColor: 'rgba(102, 187, 106, 0.45)', background: 'rgba(102, 187, 106, 0.1)'} : undefined}>
                    {p.class && <img className={classes.classIcon} style={{width: 18, height: 18}} src={classImages.getColorIcon(p.class as AocClasses)} />}
                    {p.name}
                    {ok && <SenderMark live />}
                    <span style={{color: ok ? '#81c784' : MUTED}}>{ok ? 'inside' : 'not inside yet'}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      }

      {live && !waiting && senders.length > 0 &&
        <div className={classes.waitCard} style={{alignItems: 'center'}}>
          <CloudUpload style={{color: '#4fc3f7'}} />
          <div style={{minWidth: 0}}>
            <div className={classes.waitText} style={{marginTop: 0}}>
              Sending the combat log: the owner plus up to 6 others, picked by the server. Someone who leaves, goes offline or stops sending is replaced.
            </div>
            <div className={classes.waitPlayers}>
              {senders.map((name) => {
                const p = players.find((player) => player.name == name);
                return (
                  <span key={name} className={classes.waitPlayer} style={{color: '#b3e5fc', borderColor: 'rgba(79, 195, 247, 0.45)', background: 'rgba(79, 195, 247, 0.1)'}}>
                    {p?.class && <img className={classes.classIcon} style={{width: 18, height: 18}} src={classImages.getColorIcon(p.class as AocClasses)} />}
                    {name}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      }

      {hasHardMode &&
        <div className={clsx(classes.hardInfo, {[classes.hardInfoInvalid]: invalid})}>
          <div className={classes.hardIcon}><Whatshot /></div>
          <div>
            <div className={classes.hardTitle}>
              {hardModeMissed ? 'Hard mode was not met: not on the leaderboards' : invalid ? `${finalBoss || 'The final boss'} was not killed: not on the leaderboards` : pve.hardMode ? 'Hard mode run' : 'Hard mode required'}
            </div>
            <div className={classes.hardText}>
              {instance?.hardModeActivation || 'This dungeon has a hard mode.'} Only runs completed in hard mode{instance?.requireClear ? ` (all the way through ${finalBoss || 'the final boss'})` : ''} can be verified and appear on the leaderboards.
            </div>
            <div className={classes.hardMeta}>
              Detected when {(instance?.hardMode || []).map(describePveHardModeRule).join(', or ')}.
              {pve.hardMode ? ` Detected: ${detectedText}.` : live ? ' Not detected yet.' : ' It was never detected in this run, so the run is still shown here but kept off the leaderboards.'}
            </div>
          </div>
        </div>
      }

      {encounterStats.length > 0 &&
        <div className={classes.panel}>
          <div className={classes.panelHead}><span className={classes.panelTitle}>Bosses</span><span /></div>
          <div className={classes.panelBody}>
            <div className={classes.encounters}>
              {encounterStats.map(({encounter, attempts, kill}) => {
                const color = kill ? PULL_COLORS.kill : attempts > 0 ? PULL_COLORS.wipe : '#616161';
                return (
                  <Tooltip key={encounter.id} title={attempts > 0 ? 'Select this boss\'s pulls' : ''}>
                    <div className={clsx(classes.encounter, {[classes.encounterActive]: attempts > 0})}
                      onClick={() => attempts > 0 && setSelection(pulls.filter((p) => (p.encounters || []).indexOf(encounter.id) != -1).map((p) => p.index))}
                      style={{cursor: attempts > 0 ? 'pointer' : 'default', boxShadow: `inset 3px 0 0 ${color}`}}>
                      <span className={classes.encounterDot} style={{background: color, boxShadow: attempts > 0 ? `0 0 8px ${color}` : 'none'}} />
                      <div style={{minWidth: 0}}>
                        <div className={classes.encounterName} title={encounter.name}>
                          {encounter.name}
                          {encounter.final && <span className={classes.encounterTag}>Final</span>}
                          {encounter.optional && <span className={classes.encounterTag} style={{color: '#b0bec5', background: 'rgba(176, 190, 197, 0.14)'}}>Optional</span>}
                        </div>
                        <div className={classes.encounterMeta}>
                          {kill ? <span style={{color: '#81c784'}}>Killed in {formatPveDuration(kill.duration)}</span> : attempts > 0 ? <span style={{color: '#ef9a9a'}}>Not killed</span> : 'Not engaged'}
                          {attempts > 0 ? ` · ${attempts} ${attempts == 1 ? 'pull' : 'pulls'}` : ''}
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      }

      <div className={classes.panel}>
        <div className={classes.panelHead}>
          <span className={classes.panelTitle}>Pulls<span className={classes.panelHint}>click to select or unselect; selected pulls are combined</span></span>
          <div className={classes.tools}>
            <Button className={clsx(classes.toolButton, {[classes.toolButtonActive]: selection == null})} onClick={() => setSelection(null)}>Select all</Button>
            <Button className={classes.toolButton} disabled={selected.length == 0} onClick={() => setSelection([])}>Clear</Button>
          </div>
        </div>
        <div className={classes.panelBody}>
        {pulls.length == 0 ?
          <div className={classes.empty}>{live ? 'Waiting for the first fight…' : 'No fights were recorded.'}</div>
        :
          <React.Fragment>
            <div className={classes.timeline}>
              {pulls.map((pull, i) => {
                // gaps between pulls show as empty space so the bar matches the real timeline
                const gap = i > 0 ? Math.max(0, pull.start_time - pulls[i - 1].end_time) : 0;
                return (
                  <React.Fragment key={pull.index}>
                    {gap > 0 && <div style={{width: `${(gap / span) * 100}%`}} />}
                    <Tooltip title={`${getPullLabel(pull)}${pull.result ? ` (${pull.result})` : ''} · ${formatPveDuration(pull.duration)}`}>
                      <div
                        className={classes.segment}
                        onClick={() => toggle(pull.index)}
                        style={{
                          width: `${(Math.max(pull.end_time - pull.start_time, 0) / span) * 100}%`,
                          background: getPullColor(pull),
                          opacity: isSelected(pull.index) ? 1 : 0.3,
                        }}
                      />
                    </Tooltip>
                  </React.Fragment>
                );
              })}
            </div>
            <div className={classes.pullChips}>
              {pulls.map((pull) => {
                const color = getPullColor(pull);
                const on = isSelected(pull.index);
                return (
                  <div key={pull.index} role="button" className={classes.pullChip} onClick={() => toggle(pull.index)}
                    style={{borderColor: on ? color : 'rgba(255, 255, 255, 0.1)', background: on ? `${color}33` : 'rgba(255, 255, 255, 0.025)', color: on ? '#fff' : MUTED}}>
                    <span className={classes.pullDot} style={{background: color, opacity: on ? 1 : 0.5}} />
                    <span className={classes.pullLabel} title={getPullLabel(pull)}>{getPullLabel(pull)} · {formatPveDuration(pull.duration)}{pull.type == 'boss' && pull.result == null ? ' · fighting' : ''}</span>
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        }
        </div>
      </div>

      <div className={classes.panel}>
        <div className={classes.panelHead}>
        <span className={clsx(classes.panelTitle, classes.panelTitleStrong)}>
          {selection == null || selected.length == pulls.length
            ? 'Whole run'
            : selectedPulls.length == 1
              ? `${getPullLabel(selectedPulls[0])}${selectedPulls[0].result ? ` (${selectedPulls[0].result})` : ''}`
              : `${selectedPulls.length} pulls`}
          {selectedPulls.length > 0 && <span className={classes.panelHint}>{formatPveDuration(selectedDuration)} of fighting</span>}
        </span>
        <span />
        </div>
        <div className={classes.panelBody}>
        {selectedPulls.filter((p) => p.type == 'boss').map((pull) => (pull.bosses || []).length > 0 &&
          <div key={pull.index} className={classes.bossLine}>
            <b>{getPullLabel(pull)}</b>
            {(pull.bosses || []).map((boss) => (
              <span key={boss.name}>
                {boss.name} <span className={classes.number}>{formatPveNumber(boss.damageTaken)}</span> taken{boss.death_time ? <span style={{color: '#81c784'}}>, died at {formatPveDuration((boss.death_time - pull.start_time) / 1000)}</span> : ''}
              </span>
            ))}
          </div>
        )}
        {selectedPulls.length == 0
          ? <div className={classes.empty}>Select one or more pulls.</div>
          : <PveMeter rows={combinedRows(selectedPulls)} players={players} showBossDamage={selectedPulls.some((p) => p.type == 'boss')} senders={pve.senders || []} live={live} />
        }
        </div>
      </div>

      {selectedPulls.length > 0 &&
        <div className={classes.panel}>
          <div className={classes.panelHead}><span className={classes.panelTitle}>Breakdown</span><span /></div>
          <div className={classes.panelBody}>
            <PveBreakdown pve={pve} indexes={selected} duration={selectedDuration} breakdowns={breakdowns} />
          </div>
        </div>
      }
    </Box>
  );
}
